import React from 'react';

/* Design tokens (owned by mobile-ux). Pages and shared components read colors, type,
   spacing, radii and shadows from here instead of writing values inline.
   Look: "app-native" (tinted page, borderless white cards with soft shadows, pill tabs).
   Contrast: every text color below passes WCAG AA on white at its intended size. */

export const FONT_FAMILY = 'Heebo, sans-serif';

export const color = {
  // Surfaces
  page: '#eef2f7',          // page background behind cards
  surface: '#ffffff',       // cards, sheets, the bottom nav
  surfaceMuted: '#f2f4f7',  // idle tiles and inputs inside a card, neutral chips
  track: '#e1e7ef',         // segmented-control track, empty bar track on the page
  skeleton: '#e4e9f0',

  // Text
  text: '#101828',
  textSecondary: '#475467', // meta lines, labels
  textTertiary: '#667085',  // captions and placeholders, 12px minimum
  textOnPrimary: '#ffffff',

  // Lines
  border: '#e4e7ec',
  divider: '#eef0f3',

  // Interactive (blue only; never orange)
  primary: '#185FA5',
  primaryPressed: '#0C447C',
  primarySoft: '#E6F1FB',
  primaryInk: '#0C447C',

  // Politics
  coalition: '#185FA5',
  opposition: '#E24B4A',     // bars and dots
  oppositionText: '#B42D2C', // opposition as text
  live: '#E24B4A',
  present: '#3B8C2A',        // in the Knesset building now: dots (text uses tone.success)
} as const;

/* Status tones for badges. These are the CLAUDE.md pairs; orange (warning) is for
   status only, never for something clickable. */
export type Tone = 'info' | 'success' | 'danger' | 'warning' | 'special' | 'neutral';

export const tone: Record<Tone, { fg: string; bg: string }> = {
  info:    { fg: '#0C447C', bg: '#E6F1FB' }, // government, early bill stages
  success: { fg: '#27500A', bg: '#EAF3DE' }, // passed, positive
  danger:  { fg: '#501313', bg: '#FCEBEB' }, // stopped, negative
  warning: { fg: '#633806', bg: '#FAEEDA' }, // readings
  special: { fg: '#6A1B9A', bg: '#F3E5F5' }, // Basic Law
  neutral: { fg: '#475467', bg: '#f2f4f7' }, // merged, converted, other
};

/* Type scale (px). 12px is the floor; nothing smaller. */
export const font = {
  display: 30, // large page title on section pages
  title: 23,   // entity name on detail pages
  heading: 17, // section headings
  body: 15,
  label: 14,   // buttons, tabs, list titles
  small: 13,   // meta lines
  caption: 12, // badges, tile labels, footnotes
} as const;

export const weight = { regular: 400, medium: 500, bold: 700 } as const;

/* Spacing on a 4px grid */
export const space = { xxs: 2, xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, xxxl: 32 } as const;

export const radius = {
  sm: 6,     // badges
  md: 10,    // segmented pill, small tiles
  lg: 14,    // inputs, stat tiles
  xl: 18,    // cards
  pill: 999,
} as const;

export const shadow = {
  card: '0 1px 2px rgba(16,24,40,0.06), 0 4px 12px rgba(16,24,40,0.04)',
  raised: '0 1px 2px rgba(16,24,40,0.06)',
  pressed: '0 1px 3px rgba(16,24,40,0.12)',
  selected: '0 2px 8px rgba(24,95,165,0.25)',
  floating: '0 8px 24px rgba(16,24,40,0.12), 0 1px 3px rgba(16,24,40,0.08)',
} as const;

/* Smallest comfortable tap target (px) */
export const TAP = 44;

/* Shared style fragments */
type TextStyle = 'pageTitle' | 'entityTitle' | 'heading' | 'body' | 'meta' | 'caption';

export const text: Record<TextStyle, React.CSSProperties> = {
  pageTitle: { fontSize: font.display, fontWeight: weight.bold, color: color.text, letterSpacing: -0.3, lineHeight: 1.15, margin: 0 },
  entityTitle: { fontSize: font.title, fontWeight: weight.bold, color: color.text, lineHeight: 1.2, margin: 0 },
  heading: { fontSize: font.heading, fontWeight: weight.bold, color: color.text, margin: 0 },
  body: { fontSize: font.body, color: color.text, lineHeight: 1.5 },
  meta: { fontSize: font.small, color: color.textSecondary },
  caption: { fontSize: font.caption, color: color.textTertiary },
};

// Text for screen readers only
export const visuallyHidden: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

// Resets a native <button> so it can be styled from scratch
export const buttonReset: React.CSSProperties = {
  border: 0,
  background: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'inherit',
  cursor: 'pointer',
};
